// Medical Tracker — the coach dashboard's "Medical" tab. Self-fetching (like
// SyllabusTracker/TaskTracker) so it can pull the full check-in list instead
// of the pre-formatted rows the old generic table used, and — new for
// coaches — lets them record a check-in themselves. Reuses the same
// SymptomsSelector / MultipleDoctorVisitsSection / MultipleFollowUpsSection
// sub-components the Medical Incharge's own check-in form already uses, so
// the underlying data (and its validation) stays identical across both.
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './MedicalTracker.css';
import {
    HeartPulse,
    Search,
    Users2,
    ChevronDown,
    Eye,
    Pencil,
    Trash2,
    Plus,
    PlusCircle,
    X,
    Stethoscope,
} from 'lucide-react';
import {
    getMedicalConditionBasedOnBalagruha,
    createMedicalCheckin,
    updateMedicalCheckin,
    deleteMedicalCheckin,
    getAnyUserBasedonRoleandBalagruha,
} from '../../api';
import showToast from '../../utils/toast';
import SymptomsSelector from './SymptomsSelector';
import MultipleDoctorVisitsSection from './MultipleDoctorVisitsSection';
import MultipleFollowUpsSection from './MultipleFollowUpsSection';
import SearchableSelect from '../common/SearchableSelect';

const ITEMS_PER_PAGE = 10;

const SYMPTOM_OPTIONS = [
    { value: 'cough_cold', label: 'Cough + Cold' },
    { value: 'fever', label: 'Fever' },
    { value: 'stomach_ache', label: 'Stomach ache' },
    { value: 'headache', label: 'Headache' },
    { value: 'injury', label: 'Injury' },
    { value: 'other', label: 'Other' },
];
const SYMPTOM_LABEL = SYMPTOM_OPTIONS.reduce((acc, s) => ({ ...acc, [s.value]: s.label }), {});

const STATUS_OPTIONS = [
    { value: 'open', label: 'Open' },
    { value: 'under_treatment', label: 'Under Treatment' },
    { value: 'closed', label: 'Closed' },
];

const formatDate = (date) => {
    if (!date) return '-';
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const toDateInputValue = (date) => {
    if (!date) return '';
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return '';
    return d.toISOString().split('T')[0];
};

const getStudentName = (item) => item?.userName || item?.studentId?.name || 'N/A';

const getSymptomsText = (item) => {
    const list = Array.isArray(item.symptoms) ? item.symptoms.filter((s) => s && s !== 'other') : [];
    const labels = list.map((s) => SYMPTOM_LABEL[s] || s);
    if (item.customSymptom) labels.push(item.customSymptom);
    return labels.length > 0 ? labels.join(', ') : '-';
};

const getDoctorVisitsList = (item) => (Array.isArray(item.doctorVisits) ? item.doctorVisits.filter(Boolean) : []);
const getLatestDoctorVisit = (item) => {
    const visits = getDoctorVisitsList(item);
    return visits.length > 0 ? visits[visits.length - 1] : null;
};
const getFollowUpsList = (item) => (Array.isArray(item.followUps) ? item.followUps.filter(Boolean) : []);
const getLatestFollowUp = (item) => {
    const followUps = getFollowUpsList(item);
    return followUps.length > 0 ? followUps[followUps.length - 1] : null;
};

const getDrVisitText = (item) => {
    const visit = getLatestDoctorVisit(item);
    if (!visit) return '-';
    const place = [visit.doctorName, visit.hospitalName].filter(Boolean).join(' / ');
    return place ? `${place}${visit.visitDate ? ` (${formatDate(visit.visitDate)})` : ''}` : '-';
};

const getTestsText = (item) => {
    const visit = getLatestDoctorVisit(item);
    return visit?.testDetails || '-';
};

const getFollowUpText = (item) => {
    const followUp = getLatestFollowUp(item);
    if (!followUp) return '-';
    if (followUp.status === 'completed') return 'Completed';
    if (followUp.followUpDate) return `Follow up on ${formatDate(followUp.followUpDate)}`;
    if (followUp.status === 'active') return 'Under treatment';
    return followUp.notes || '-';
};

const STATUS_LABEL = { open: 'Open', under_treatment: 'Under Treatment', closed: 'Closed' };
const getStatusTone = (status) => (status === 'closed' ? 'closed' : status === 'under_treatment' ? 'under-treatment' : 'open');

const formatAmount = (amount) => `₹${Number(amount).toLocaleString('en-IN')}`;

// A File is one the coach just picked; anything else in those arrays is a file
// already saved on the check-in (an object with a fileUrl).
const isNewFile = (file) => file instanceof File;
const uploadedFiles = (files) => (files || []).filter((file) => !isNewFile(file));
const countNewFiles = (files) => (files || []).filter(isNewFile).length;
const FILE_KEYS = ['prescriptionFiles', 'testResultFiles', 'descriptionFiles'];

// An untouched amount input is '' — "not recorded", so leave it out rather than send 0.
const toAmount = (value) => (value === '' || value == null ? undefined : Number(value));

// Shapes one doctor visit / follow-up for the API; create and edit share it so
// the two can't drift apart.
const serializeDoctorVisit = (v) => ({
    doctorName: v.doctorName, hospitalName: v.hospitalName, visitDate: v.visitDate,
    testDetails: v.testDetails, conclusion: v.conclusion,
    consultationNotes: v.consultationNotes, amountSpent: toAmount(v.amountSpent),
});
const serializeFollowUp = (f) => ({
    followUpDate: f.followUpDate, hospital: f.hospital, doctor: f.doctor,
    assignedCoaches: f.assignedCoaches, status: f.status, notes: f.notes,
    amountSpent: toAmount(f.amountSpent),
});

const emptyFormState = {
    checkInId: null,
    balagruhaId: '',
    studentId: '',
    temperature: '',
    date: new Date().toISOString().split('T')[0],
    time: new Date().toTimeString().slice(0, 5),
    healthStatus: 'normal',
    symptoms: [],
    customSymptom: '',
    medicines: '',
    doctorVisits: [],
    followUps: [],
    status: 'open',
    assignedTo: '',
    notes: '',
};

// Create/Edit form. Reuses the Medical Incharge's own SymptomsSelector /
// MultipleDoctorVisitsSection / MultipleFollowUpsSection sub-components so
// symptom values, doctor-visit shape, and follow-up shape (and their file
// uploads) stay byte-identical to what the rest of the app already saves.
export function CheckInFormModal({ initialCheckIn, balagruhas, users, onClose, onSaved, sectionVariant = 'modern' }) {
    const [form, setForm] = useState(emptyFormState);
    const [students, setStudents] = useState([]);
    const [saving, setSaving] = useState(false);
    const isEdit = !!initialCheckIn;

    useEffect(() => {
        if (initialCheckIn) {
            const studentBalagruhaId = Array.isArray(initialCheckIn.balagruhaIds) && initialCheckIn.balagruhaIds.length > 0
                ? (typeof initialCheckIn.balagruhaIds[0] === 'object' ? initialCheckIn.balagruhaIds[0]._id : initialCheckIn.balagruhaIds[0])
                : (balagruhas?.[0]?._id || '');
            setForm({
                checkInId: initialCheckIn._id,
                balagruhaId: studentBalagruhaId,
                studentId: initialCheckIn.studentId,
                temperature: initialCheckIn.temperature ?? '',
                date: toDateInputValue(initialCheckIn.date) || emptyFormState.date,
                time: initialCheckIn.date ? new Date(initialCheckIn.date).toTimeString().slice(0, 5) : emptyFormState.time,
                healthStatus: initialCheckIn.healthStatus || 'normal',
                symptoms: initialCheckIn.symptoms || [],
                customSymptom: initialCheckIn.customSymptom || '',
                medicines: initialCheckIn.medicines || '',
                doctorVisits: (initialCheckIn.doctorVisits || []).map((v) => ({
                    ...v,
                    visitDate: toDateInputValue(v.visitDate),
                })),
                followUps: (initialCheckIn.followUps || []).map((f) => ({
                    ...f,
                    followUpDate: toDateInputValue(f.followUpDate),
                })),
                status: initialCheckIn.status || 'open',
                assignedTo: initialCheckIn.assignedTo?._id || initialCheckIn.assignedTo || '',
                notes: initialCheckIn.notes || '',
            });
        } else {
            setForm({ ...emptyFormState, balagruhaId: balagruhas?.[0]?._id || '' });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialCheckIn]);

    const fetchStudents = async (balId) => {
        if (!balId) { setStudents([]); return; }
        try {
            const response = await getAnyUserBasedonRoleandBalagruha('student', balId);
            setStudents(response?.success ? (response?.data?.users || []) : []);
        } catch (error) {
            setStudents([]);
        }
    };

    useEffect(() => {
        fetchStudents(form.balagruhaId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form.balagruhaId]);

    const update = (patch) => setForm((prev) => ({ ...prev, ...patch }));

    const today = new Date().toISOString().split('T')[0];

    const validate = () => {
        if (!form.balagruhaId) { showToast('Please select a Balagruha', 'error'); return false; }
        if (!form.studentId) { showToast('Please select a child', 'error'); return false; }
        if (!form.symptoms.length) { showToast('Please select at least one symptom', 'error'); return false; }
        if (form.symptoms.includes('other') && !form.customSymptom.trim()) {
            showToast('Please describe the custom symptom', 'error');
            return false;
        }
        if (!form.date) { showToast('Please select the check-in date', 'error'); return false; }
        if (form.date > today) { showToast('Check-in date cannot be in the future', 'error'); return false; }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate() || saving) return;
        setSaving(true);
        try {
            if (isEdit) {
                const updateData = {
                    studentId: form.studentId,
                    temperature: form.temperature,
                    date: `${form.date} ${form.time}`,
                    healthStatus: form.healthStatus,
                    notes: form.notes,
                    symptoms: form.symptoms,
                    customSymptom: form.customSymptom,
                    medicines: form.medicines,
                    status: form.status,
                    assignedTo: form.assignedTo || '',
                    // Always sent, even when empty, so visits/follow-ups the coach
                    // removed are removed from the saved check-in. Files uploaded
                    // earlier are sent back as they are — the save replaces each
                    // entry, so leaving them out would delete them.
                    doctorVisits: form.doctorVisits.map((v) => ({
                        ...serializeDoctorVisit(v),
                        prescriptionFiles: uploadedFiles(v.prescriptionFiles),
                        testResultFiles: uploadedFiles(v.testResultFiles),
                    })),
                    followUps: form.followUps.map((f) => ({
                        ...serializeFollowUp(f),
                        descriptionFiles: uploadedFiles(f.descriptionFiles),
                        testResultFiles: uploadedFiles(f.testResultFiles),
                    })),
                };
                // Saving an edit is a JSON update; it can't carry new uploads.
                const hasNewFiles = [...form.doctorVisits, ...form.followUps]
                    .some((entry) => FILE_KEYS.some((key) => (entry[key] || []).some(isNewFile)));
                const response = await updateMedicalCheckin(form.checkInId, updateData);
                if (response.success) {
                    showToast(
                        hasNewFiles
                            ? 'Medical check-in updated. The newly selected files were not attached — files can only be added when recording a check-in.'
                            : 'Medical check-in updated',
                        'success'
                    );
                    onSaved();
                    onClose();
                } else {
                    showToast(response.message || 'Failed to update check-in', 'error');
                }
            } else {
                const fd = new FormData();
                fd.append('studentId', form.studentId);
                fd.append('temperature', form.temperature);
                fd.append('date', `${form.date} ${form.time}`);
                fd.append('healthStatus', form.healthStatus);
                fd.append('notes', form.notes);
                fd.append('medicines', form.medicines);
                fd.append('status', form.status);
                fd.append('assignedTo', form.assignedTo || '');
                form.symptoms.forEach((s) => fd.append('symptoms[]', s));
                fd.append('customSymptom', form.customSymptom);

                // The *Count fields say how many of the uploaded files (appended
                // below, in this same order) belong to each entry, so the server
                // files them under the right visit / follow-up.
                fd.append('doctorVisits', JSON.stringify(form.doctorVisits.map((v) => ({
                    ...serializeDoctorVisit(v),
                    prescriptionCount: countNewFiles(v.prescriptionFiles),
                    testResultCount: countNewFiles(v.testResultFiles),
                }))));
                fd.append('followUps', JSON.stringify(form.followUps.map((f) => ({
                    ...serializeFollowUp(f),
                    descriptionCount: countNewFiles(f.descriptionFiles),
                    testResultCount: countNewFiles(f.testResultFiles),
                }))));

                form.doctorVisits.forEach((v) => {
                    (v.prescriptionFiles || []).forEach((file) => { if (isNewFile(file)) fd.append('prescriptions', file); });
                    (v.testResultFiles || []).forEach((file) => { if (isNewFile(file)) fd.append('testResults', file); });
                });
                form.followUps.forEach((f) => {
                    (f.descriptionFiles || []).forEach((file) => { if (isNewFile(file)) fd.append('followUpDescriptions', file); });
                    (f.testResultFiles || []).forEach((file) => { if (isNewFile(file)) fd.append('followUpTestResults', file); });
                });

                const response = await createMedicalCheckin(fd);
                if (response.success) {
                    showToast('Medical check-in recorded', 'success');
                    onSaved();
                    onClose();
                } else {
                    showToast(response.message || 'Failed to record check-in', 'error');
                }
            }
        } catch (error) {
            showToast(error?.response?.data?.message || 'Failed to save check-in', 'error');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="medical-modal-overlay">
            <div className="medical-modal medical-form-modal">
                <div className="doctor-form-header">
                    <div className="doctor-form-header-icon"><PlusCircle size={26} /></div>
                    <div className="doctor-form-header-text">
                        <h3>{isEdit ? 'Edit Check-in' : 'Record New Check-in'}</h3>
                        <p>
                            {isEdit
                                ? 'Update health details, medicines, doctor visits and follow-ups.'
                                : 'Add health details, medicines, doctor visit and follow-up.'}
                        </p>
                    </div>
                    <button type="button" className="medical-modal-close" onClick={onClose} aria-label="Close"><X size={22} /></button>
                </div>

                <form onSubmit={handleSubmit} className="medical-form">
                    <div className="medical-form-body">
                        <div className="medical-form-row">
                            <div className="medical-form-group">
                                <label>Balagruha <span className="required">*</span></label>
                                <SearchableSelect
                                    searchPlaceholder="Search balagruha..."
                                    value={form.balagruhaId}
                                    onChange={(e) => update({ balagruhaId: e.target.value, studentId: '' })}
                                    disabled={isEdit}
                                    required
                                >
                                    <option value="">Select Balagruha</option>
                                    {(balagruhas || []).map((b) => <option key={b._id} value={b._id}>{b.name}</option>)}
                                </SearchableSelect>
                            </div>
                            <div className="medical-form-group">
                                <label>Child <span className="required">*</span></label>
                                <SearchableSelect searchPlaceholder="Search child..." value={form.studentId} onChange={(e) => update({ studentId: e.target.value })} required>
                                    <option value="">Select child</option>
                                    {students.map((s) => <option key={s._id} value={s._id}>{s.name}</option>)}
                                </SearchableSelect>
                            </div>
                        </div>

                        <div className="medical-form-row">
                            <div className="medical-form-group">
                                <label>Date <span className="required">*</span></label>
                                <input type="date" value={form.date} max={today} onChange={(e) => update({ date: e.target.value })} required />
                            </div>
                            <div className="medical-form-group">
                                <label>Time <span className="required">*</span></label>
                                <input type="time" value={form.time} onChange={(e) => update({ time: e.target.value })} required />
                            </div>
                        </div>

                        <div className="medical-form-row medical-symptoms-row">
                            <div className="medical-form-col">
                                <SymptomsSelector
                                    variant="checklist"
                                    symptoms={form.symptoms}
                                    customSymptom={form.customSymptom}
                                    onChange={(patch) => update(patch)}
                                />
                            </div>
                            <div className="medical-form-col">
                                <div className="medical-form-group">
                                    <label>Medicines</label>
                                    <input type="text" value={form.medicines} onChange={(e) => update({ medicines: e.target.value })} placeholder="e.g. Paracetamol, ORS" />
                                </div>
                                <div className="medical-form-group">
                                    <label>Health Severity <span className="required">*</span></label>
                                    <select value={form.healthStatus} onChange={(e) => update({ healthStatus: e.target.value })}>
                                        <option value="normal">Normal</option>
                                        <option value="important">Important</option>
                                        <option value="critical">Critical</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        <MultipleDoctorVisitsSection
                            variant={sectionVariant}
                            doctorVisits={form.doctorVisits}
                            onChange={(doctorVisits) => update({ doctorVisits })}
                        />

                        <MultipleFollowUpsSection
                            variant={sectionVariant}
                            followUps={form.followUps}
                            balagruhaId={form.balagruhaId}
                            onChange={(followUps) => update({ followUps })}
                        />

                        <div className="medical-form-row">
                            <div className="medical-form-group">
                                <label>Case Status <span className="required">*</span></label>
                                <select value={form.status} onChange={(e) => update({ status: e.target.value })}>
                                    {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                                </select>
                            </div>
                            <div className="medical-form-group">
                                <label>Assigned To</label>
                                <SearchableSelect searchPlaceholder="Search name..." value={form.assignedTo} onChange={(e) => update({ assignedTo: e.target.value })}>
                                    <option value="">Myself</option>
                                    {(users || []).map((u) => <option key={u._id} value={u._id}>{u.name}</option>)}
                                </SearchableSelect>
                                <small className="medical-form-hint">
                                    Creates a follow-up task for this person — or for you, if left as "Myself".
                                </small>
                            </div>
                        </div>

                        <div className="medical-form-group">
                            <label>Notes</label>
                            <textarea rows={3} value={form.notes} onChange={(e) => update({ notes: e.target.value })} placeholder="Add any additional notes..." />
                        </div>
                    </div>

                    <div className="medical-form-footer">
                        <button type="button" className="ghost" onClick={onClose} disabled={saving}>Cancel</button>
                        <button type="submit" disabled={saving}>{saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Record Check-in'}</button>
                    </div>
                </form>
            </div>
        </div>
    );
}

// Read-only detail view (the eye icon).
export function ViewCheckInModal({ checkIn, onClose }) {
    const visits = getDoctorVisitsList(checkIn);
    const followUps = getFollowUpsList(checkIn);
    return (
        <div className="medical-modal-overlay">
            <div className="medical-modal medical-view-modal">
                <div className="medical-modal-header">
                    <h3>{getStudentName(checkIn)}</h3>
                    <button type="button" className="medical-modal-close" onClick={onClose} aria-label="Close"><X size={18} /></button>
                </div>
                <div className="medical-view-body">
                    <div className="medical-view-grid">
                        <div><span>Date</span><strong>{formatDate(checkIn.date)}</strong></div>
                        <div><span>Severity</span><strong>{checkIn.healthStatus || '-'}</strong></div>
                        <div><span>Status</span><strong>{STATUS_LABEL[checkIn.status] || 'Open'}</strong></div>
                        <div><span>Temperature</span><strong>{checkIn.temperature ? `${checkIn.temperature}°C` : '-'}</strong></div>
                    </div>
                    <div className="medical-view-block">
                        <span>Symptoms</span>
                        <p>{getSymptomsText(checkIn)}</p>
                    </div>
                    <div className="medical-view-block">
                        <span>Medicines</span>
                        <p>{checkIn.medicines || '-'}</p>
                    </div>
                    <div className="medical-view-block">
                        <span>Notes</span>
                        <p>{checkIn.notes || '-'}</p>
                    </div>
                    {visits.length > 0 && (
                        <div className="medical-view-block">
                            <span>Doctor Visits</span>
                            {visits.map((v, i) => (
                                <p key={i}>
                                    {[v.doctorName, v.hospitalName].filter(Boolean).join(' / ') || 'Visit'} {v.visitDate ? `— ${formatDate(v.visitDate)}` : ''}
                                    {v.testDetails ? ` — Tests: ${v.testDetails}` : ''}
                                    {v.conclusion ? ` — ${v.conclusion}` : ''}
                                    {v.consultationNotes ? ` — Notes: ${v.consultationNotes}` : ''}
                                    {v.amountSpent != null ? ` — ${formatAmount(v.amountSpent)}` : ''}
                                </p>
                            ))}
                        </div>
                    )}
                    {followUps.length > 0 && (
                        <div className="medical-view-block">
                            <span>Follow-ups</span>
                            {followUps.map((f, i) => (
                                <p key={i}>
                                    {f.followUpDate ? formatDate(f.followUpDate) : 'No date'} — {[f.hospital, f.doctor].filter(Boolean).join(' / ') || 'N/A'}
                                    {f.status ? ` — ${f.status}` : ''}{f.notes ? ` — ${f.notes}` : ''}
                                    {f.amountSpent != null ? ` — ${formatAmount(f.amountSpent)}` : ''}
                                </p>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default function MedicalTracker({ balagruhas, selectedBalagruhas, users, canCreate, canEdit, canDelete }) {
    const navigate = useNavigate();
    const [checkIns, setCheckIns] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [symptomFilter, setSymptomFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [balagruhaFilter, setBalagruhaFilter] = useState('all');
    // Defaults to All Time (no bounds) so every check-in for the coach's
    // balagruha(s) is visible until they narrow it themselves — the check-in
    // list itself is already balagruha-scoped, not limited to the current month.
    const [dateRange, setDateRange] = useState({ start: '', end: '' });
    const [dateRangeOpen, setDateRangeOpen] = useState(false);
    const [sortKey, setSortKey] = useState('date');
    const [sortDir, setSortDir] = useState('desc');
    const [currentPage, setCurrentPage] = useState(1);

    const [showForm, setShowForm] = useState(false);
    const [editingCheckIn, setEditingCheckIn] = useState(null);
    const [viewingCheckIn, setViewingCheckIn] = useState(null);
    const [deletingId, setDeletingId] = useState(null);

    const fetchCheckIns = async () => {
        if (!selectedBalagruhas || selectedBalagruhas.length === 0) { setCheckIns([]); return; }
        setLoading(true);
        try {
            const response = await getMedicalConditionBasedOnBalagruha({ balagruhaIds: selectedBalagruhas });
            setCheckIns(response?.success ? (response.data?.medicalCheckIns || []) : []);
        } catch (error) {
            showToast('Failed to load medical check-ins', 'error');
            setCheckIns([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCheckIns();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedBalagruhas]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchText, symptomFilter, statusFilter, balagruhaFilter, dateRange]);

    const getItemBalagruhaId = (item) => {
        const raw = Array.isArray(item.balagruhaIds) && item.balagruhaIds.length > 0 ? item.balagruhaIds[0] : null;
        return typeof raw === 'object' ? raw?._id : raw;
    };

    const searchLower = searchText.trim().toLowerCase();
    const filtered = checkIns.filter((item) => {
        if (balagruhaFilter !== 'all' && String(getItemBalagruhaId(item)) !== String(balagruhaFilter)) return false;
        if (symptomFilter !== 'all' && !(item.symptoms || []).includes(symptomFilter)) return false;
        if (statusFilter !== 'all' && (item.status || 'open') !== statusFilter) return false;
        if (dateRange.start && item.date && toDateInputValue(item.date) < dateRange.start) return false;
        if (dateRange.end && item.date && toDateInputValue(item.date) > dateRange.end) return false;
        if (searchLower) {
            const haystack = `${getStudentName(item)} ${getSymptomsText(item)} ${item.medicines || ''} ${item.notes || ''}`.toLowerCase();
            if (!haystack.includes(searchLower)) return false;
        }
        return true;
    });

    const sorted = useMemo(() => {
        const list = [...filtered];
        list.sort((a, b) => {
            const valueOf = (item) => {
                if (sortKey === 'followUp') {
                    const fu = getLatestFollowUp(item);
                    return fu?.followUpDate ? new Date(fu.followUpDate).getTime() : 0;
                }
                return item.date ? new Date(item.date).getTime() : 0;
            };
            const diff = valueOf(a) - valueOf(b);
            return sortDir === 'asc' ? diff : -diff;
        });
        return list;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filtered, sortKey, sortDir]);

    const toggleSort = (key) => {
        if (sortKey === key) {
            setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortKey(key);
            setSortDir('desc');
        }
    };

    const totalPages = Math.max(1, Math.ceil(sorted.length / ITEMS_PER_PAGE));
    const pageSafe = Math.min(currentPage, totalPages);
    const paginated = sorted.slice((pageSafe - 1) * ITEMS_PER_PAGE, pageSafe * ITEMS_PER_PAGE);

    const handleDelete = async (id) => {
        try {
            const response = await deleteMedicalCheckin(id);
            if (response.success) {
                showToast('Check-in deleted', 'success');
                setDeletingId(null);
                fetchCheckIns();
            } else {
                showToast(response.message || 'Failed to delete check-in', 'error');
            }
        } catch (error) {
            showToast(error?.response?.data?.message || 'Failed to delete check-in', 'error');
        }
    };

    const dateRangeLabel = (!dateRange.start && !dateRange.end)
        ? 'All Time'
        : `${formatDate(dateRange.start)} - ${formatDate(dateRange.end)}`;

    return (
        <div className="medical-tracker">
            <div className="medical-header">
                <div className="medical-title">
                    <span className="medical-title-icon" aria-hidden="true"><HeartPulse size={24} color="#e0405a" strokeWidth={2.25} /></span>
                    <div>
                        <h3>Medical</h3>
                        <div className="medical-subtitle">Healthier Children, Brighter Tomorrows</div>
                    </div>
                </div>
                <div className="medical-date-range">
                    <button type="button" className="medical-date-range-btn" onClick={() => setDateRangeOpen((v) => !v)}>
                        {dateRangeLabel} <ChevronDown size={14} />
                    </button>
                    {dateRangeOpen && (
                        <div className="medical-date-range-panel">
                            <label>Start<input type="date" value={dateRange.start} onChange={(e) => setDateRange((r) => ({ ...r, start: e.target.value }))} /></label>
                            <label>End<input type="date" value={dateRange.end} onChange={(e) => setDateRange((r) => ({ ...r, end: e.target.value }))} /></label>
                            <button type="button" onClick={() => setDateRangeOpen(false)}>Apply</button>
                        </div>
                    )}
                </div>
            </div>

            <div className="medical-toolbar">
                <div className="medical-search">
                    <Search size={16} className="medical-search-icon" aria-hidden="true" />
                    <input type="text" placeholder="Search by name, symptoms, medicine..." value={searchText} onChange={(e) => setSearchText(e.target.value)} />
                </div>

                <div className="medical-filter">
                    <select className="medical-filter-select" value={symptomFilter} onChange={(e) => setSymptomFilter(e.target.value)}>
                        <option value="all">All Symptoms</option>
                        {SYMPTOM_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                    <ChevronDown size={14} className="medical-filter-chevron" aria-hidden="true" />
                </div>

                <div className="medical-filter">
                    <select className="medical-filter-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                        <option value="all">All Status</option>
                        {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                    <ChevronDown size={14} className="medical-filter-chevron" aria-hidden="true" />
                </div>

                <div className="medical-filter">
                    <SearchableSelect searchPlaceholder="Search balagruha..." className="medical-filter-select" value={balagruhaFilter} onChange={(e) => setBalagruhaFilter(e.target.value)}>
                        <option value="all">All Balagruhas</option>
                        {(balagruhas || []).map((b) => <option key={b._id} value={b._id}>{b.name}</option>)}
                    </SearchableSelect>
                    <ChevronDown size={14} className="medical-filter-chevron" aria-hidden="true" />
                </div>

                <button type="button" className="medical-doctor-bank-btn" onClick={() => navigate('/medical/doctor-bank')}>
                    <Users2 size={16} /> Doctor Bank
                </button>

                {canCreate && (
                    <button type="button" className="medical-add-btn" onClick={() => { setEditingCheckIn(null); setShowForm(true); }}>
                        <Plus size={16} /> Record New Check-in
                    </button>
                )}
            </div>

            <div className="medical-card">
                <div className="medical-table-wrapper">
                    <table className="medical-table">
                        <thead>
                            <tr>
                                <th>Sr. No</th>
                                <th>Name</th>
                                <th className="sortable" onClick={() => toggleSort('date')}>Date {sortKey === 'date' ? (sortDir === 'asc' ? '↑' : '↓') : '⇅'}</th>
                                <th>Symptoms</th>
                                <th>Medicines</th>
                                <th>Tests</th>
                                <th>Dr. Visit</th>
                                <th className="sortable" onClick={() => toggleSort('followUp')}>Follow Up {sortKey === 'followUp' ? (sortDir === 'asc' ? '↑' : '↓') : '⇅'}</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && (
                                <tr><td colSpan={10} className="medical-empty">Loading...</td></tr>
                            )}
                            {!loading && paginated.length === 0 && (
                                <tr><td colSpan={10} className="medical-empty">No medical records found</td></tr>
                            )}
                            {!loading && paginated.map((item, index) => (
                                <tr key={item._id} className={index % 2 === 0 ? 'even-row' : 'odd-row'}>
                                    <td>{(pageSafe - 1) * ITEMS_PER_PAGE + index + 1}</td>
                                    <td className="medical-name-cell">{getStudentName(item)}</td>
                                    <td>{formatDate(item.date)}</td>
                                    <td>{getSymptomsText(item)}</td>
                                    <td>{item.medicines || '-'}</td>
                                    <td>{getTestsText(item)}</td>
                                    <td>{getDrVisitText(item)}</td>
                                    <td>
                                        <span className={`medical-chip tone-${getStatusTone(getLatestFollowUp(item)?.status === 'completed' ? 'closed' : item.status)}`}>
                                            {getFollowUpText(item)}
                                        </span>
                                    </td>
                                    <td>
                                        <span className={`medical-status-pill tone-${getStatusTone(item.status)}`}>
                                            {STATUS_LABEL[item.status] || 'Open'}
                                        </span>
                                    </td>
                                    <td className="medical-actions-cell">
                                        <button type="button" onClick={() => setViewingCheckIn(item)} aria-label="View" title="View"><Eye size={15} /></button>
                                        {canEdit && (
                                            <button type="button" onClick={() => { setEditingCheckIn(item); setShowForm(true); }} aria-label="Edit" title="Edit"><Pencil size={14} /></button>
                                        )}
                                        {canDelete && (
                                            <button type="button" onClick={() => setDeletingId(item._id)} aria-label="Delete" title="Delete" className="danger"><Trash2 size={14} /></button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="medical-footer">
                    <div className="medical-footer-info">
                        {sorted.length === 0
                            ? 'Showing 0 records'
                            : `Showing ${(pageSafe - 1) * ITEMS_PER_PAGE + 1} to ${Math.min(pageSafe * ITEMS_PER_PAGE, sorted.length)} of ${sorted.length} records`}
                    </div>
                    {totalPages > 1 && (
                        <div className="medical-pagination">
                            <button disabled={pageSafe === 1} onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}>&lsaquo;</button>
                            {Array.from({ length: totalPages }).map((_, i) => (
                                <button key={i} className={pageSafe === i + 1 ? 'active' : ''} onClick={() => setCurrentPage(i + 1)}>{i + 1}</button>
                            ))}
                            <button disabled={pageSafe === totalPages} onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}>&rsaquo;</button>
                        </div>
                    )}
                </div>
            </div>

            {showForm && (
                <CheckInFormModal
                    initialCheckIn={editingCheckIn}
                    balagruhas={balagruhas}
                    users={users}
                    onClose={() => { setShowForm(false); setEditingCheckIn(null); }}
                    onSaved={fetchCheckIns}
                />
            )}

            {viewingCheckIn && (
                <ViewCheckInModal checkIn={viewingCheckIn} onClose={() => setViewingCheckIn(null)} />
            )}

            {deletingId && (
                <div className="medical-modal-overlay">
                    <div className="medical-modal medical-confirm-modal">
                        <h3><Stethoscope size={18} /> Delete check-in?</h3>
                        <p>This will permanently remove this medical check-in record.</p>
                        <div className="medical-confirm-actions">
                            <button type="button" className="ghost" onClick={() => setDeletingId(null)}>Cancel</button>
                            <button type="button" className="danger" onClick={() => handleDelete(deletingId)}>Delete</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
